/**
 * RDV en ligne (#429) — Story 3.5a (#556). Public slot-proposals contract.
 *
 * The public booking browser asks for available slots given the picked services
 * and an exploitable client address. The server runs the planning engine
 * (#489) and answers with a DELIBERATELY REDUCED shape: bare slots only.
 *
 * The engine's `Proposal` carries `dayContext` (neighbors + travel times) and
 * `marginalDetourMinutes` — the optimisation internals that rank the slots.
 * Exposing any of it would leak the pro's tour geometry (other clients'
 * addresses, route detours) to an unauthenticated caller (FR25). So the public
 * surface keeps only `{start, end}` per slot; the ranking order is preserved
 * (the engine already sorts), but the scores that produced it are stripped.
 *
 * The two no-slot business outcomes (`out_of_zone`, `no_availability`) pass
 * through verbatim: the FE (3.5b) reads `kind` to show the phone CTA and
 * persists nothing (FR31/FR32) — this endpoint never creates an `RdvRequest`.
 *
 * See `docs/spec/rdv-en-ligne/adr-moteur-et-contrats.md`.
 */
import { z } from "zod";
import { PUBLIC_API_ADDRESS_MAX, PUBLIC_API_EMAIL_MAX, PUBLIC_API_SERVICE_IDS_MAX, } from "../publicApi/bounds.js";
import { buildServiceLinesSchema } from "./serviceLines.js";
/**
 * Request payload for `POST /api/pro/:slug/proposals`.
 *
 * Mirrors the relevant subset of the 3.6 submission payload (same field types).
 * The address fields are OPTIONAL here because they are only sent when the
 * client is NOT recognized (or recognized with an inexploitable stored address):
 * a recognized fiche keeps its address server-side (assumed opacity, never sent
 * back to the browser), so the server re-matches the email and resolves the
 * coordinates from the fiche instead. The service — not this schema — enforces
 * that the body carries usable coordinates when no fiche provides them, because
 * "recognized" is server-side state Zod cannot see. Presence is re-gated
 * server-side (`resolveReliablePointWithoutPrecision`, #797 — the score no longer
 * gates this path); the server never re-geocodes (the address was already
 * qualified upstream at 3.4).
 */
// Anti-abuse bounds shared with the other public payloads — see `publicApi/bounds.ts` (#622).
//
// The service basket (#711) is `services: {serviceDescriptionId, quantity}[]`
// (required — see `buildServiceLinesSchema`, #724 dropped the legacy
// `serviceDescriptionIds: string[]` shape once the FE migration fully rolled out).
export const RdvProposalsPayloadSchema = buildServiceLinesSchema({
    email: z.string().email().max(PUBLIC_API_EMAIL_MAX), // re-matched server-side to resolve a recognized fiche
    serviceAddress: z.string().max(PUBLIC_API_ADDRESS_MAX).optional(),
    serviceLat: z.number().finite().min(-90).max(90).optional(),
    serviceLng: z.number().finite().min(-180).max(180).optional(),
    serviceGeocodeScore: z.number().finite().min(0).max(1).optional(),
}, { maxLines: PUBLIC_API_SERVICE_IDS_MAX });
/**
 * Map the engine result to the public response, stripping every optimisation
 * internal (`dayContext`, `marginalDetourMinutes`) and keeping only the bare
 * `{start, end}` slot. Pure — the heart of the FR24/FR25 privacy boundary, so
 * it is unit-tested in isolation (no DB / engine harness needed).
 */
export function toPublicProposalsResponse(result) {
    if (result.kind === "proposals") {
        const recommendedIndex = pickRecommendedIndex(result.proposals);
        return {
            kind: "proposals",
            slots: result.proposals.map((proposal, index) => ({
                start: proposal.startAt,
                end: proposal.endAt,
                ...(index === recommendedIndex ? { recommended: true } : {}),
            })),
        };
    }
    return result;
}
/**
 * Index of the recommended proposal (#713): strict minimal detour — NOT the
 * D12 tie band used for ordering — with the earliest start date breaking an
 * exact tie. Returns -1 on an empty list (no slot flagged).
 */
function pickRecommendedIndex(proposals) {
    let bestIndex = -1;
    let best = null;
    proposals.forEach((proposal, index) => {
        if (best === null ||
            proposal.marginalDetourMinutes < best.marginalDetourMinutes ||
            (proposal.marginalDetourMinutes === best.marginalDetourMinutes &&
                proposal.startAt < best.startAt)) {
            best = proposal;
            bestIndex = index;
        }
    });
    return bestIndex;
}
//# sourceMappingURL=proposals.js.map